import { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import {
  Home,
  Users,
  Calendar,
  Heart,
  FileText,
  Image,
  Bell,
  Settings,
  LogOut,
  Menu,
  X,
  Phone,
  BarChart,
  Upload,
  UtensilsCrossed,
  BookOpen,
  Camera,
  ShieldAlert,
} from "lucide-react";
import useHostelSettings from "@/hooks/useHostelSettings";
import useWardenPermissions from "@/hooks/useWardenPermissions";
import apiClient from "@/utils/apiClient";
import { Helmet } from "react-helmet";
// Import warden sub-pages
import WardenHome from "@/components/warden/WardenHome";
import StudentManagement from "@/components/warden/StudentManagement";
import StudentProfile from "@/components/warden/StudentProfile";
import AttendanceManagement from "@/components/warden/AttendanceManagement";
import HealthRecords from "@/components/warden/HealthRecords";
import MarkSheets from "@/components/warden/MarkSheets";
import ResultsManagement from "@/components/warden/ResultsManagement";
import Activities from "@/components/warden/Activities";
import AnnouncementsBoard from "@/components/warden/AnnouncementsBoard";
import CallRequests from "@/components/warden/CallRequests";
import WardenIssues from "@/components/warden/WardenIssues";
import WardenSettings from "@/components/warden/WardenSettings";
import BulkUpdates from "@/components/warden/BulkUpdates";
import WardenNutrition from "@/components/warden/WardenNutrition";
import WardenAcademic from "@/components/warden/WardenAcademic";
import WardenGallery from "@/components/warden/WardenGallery";
import HolidayView from "@/components/shared/HolidayView";
import CommonPasswordForgotModal from "@/components/common/CommonPasswordForgotModal";

const WardenDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { settings, logoUrl, shortAddress } = useHostelSettings();
  const {
    permissions,
    loading: permissionsLoading,
    hasPermission,
  } = useWardenPermissions();

  // Update favicon when logo changes
  useEffect(() => {
    if (logoUrl) {
      let favicon = document.querySelector('link[rel="icon"]');
      if (!favicon) {
        favicon = document.createElement("link");
        favicon.rel = "icon";
        document.head.appendChild(favicon);
      }
      favicon.href = logoUrl;
    }
  }, [logoUrl]);

  // Base menu items - Dashboard and Students are always visible
  const allMenuItems = [
    {
      icon: Home,
      label: "Dashboard",
      path: "/warden",
      testId: "nav-dashboard",
      alwaysShow: true,
    },
    {
      icon: Users,
      label: "My Students",
      path: "/warden/students",
      testId: "nav-students",
      alwaysShow: true,
    },
    {
      icon: Calendar,
      label: "Attendance",
      path: "/warden/attendance",
      testId: "nav-attendance",
      permission: "can_update_attendance",
    },
    {
      icon: Heart,
      label: "Health Records",
      path: "/warden/health",
      testId: "nav-health",
      permission: "can_update_health",
    },
    {
      icon: Image,
      label: "Activities",
      path: "/warden/activities",
      testId: "nav-activities",
      permission: "can_update_activities",
    },
    {
      icon: UtensilsCrossed,
      label: "Nutrition",
      path: "/warden/nutrition",
      testId: "nav-nutrition",
      alwaysShow: true,
    },
    {
      icon: BookOpen,
      label: "Academic",
      path: "/warden/academic",
      testId: "nav-academic",
      alwaysShow: true,
    },
    {
      icon: FileText,
      label: "Results",
      path: "/warden/results",
      testId: "nav-results",
      alwaysShow: true,
    },
    {
      icon: Bell,
      label: "Notice Board",
      path: "/warden/announcements",
      testId: "nav-announcements",
      permission: "can_send_notifications",
    },
    {
      icon: Phone,
      label: "Parent Requests",
      path: "/warden/calls",
      testId: "nav-calls",
      alwaysShow: true,
    },
    {
      icon: ShieldAlert,
      label: "Warden Issues",
      path: "/warden/issues",
      testId: "nav-warden-issues",
      alwaysShow: true,
    },
    {
      icon: Camera,
      label: "School Gallery",
      path: "/warden/gallery",
      testId: "nav-gallery",
      alwaysShow: true,
    },
    {
      icon: Calendar,
      label: "Holidays",
      path: "/warden/holidays",
      testId: "nav-holidays",
      alwaysShow: true,
    },
  ];

  // Filter menu items based on permissions
  const menuItems = permissionsLoading
    ? allMenuItems.filter((item) => item.alwaysShow) // Show only basic items while loading
    : allMenuItems.filter(
        (item) =>
          item.alwaysShow || (item.permission && hasPermission(item.permission))
      );

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <>
      <Helmet>
        <title>{process.env.REACT_APP_NAME} || Warden</title>
        <link rel="icon" href="/favicon.webp" type="image/webp" />
      </Helmet>
      <div className="flex h-screen" style={{ background: "#F8FBFD" }}>
        {/* Sidebar */}
        <div
          className={`fixed lg:static inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
          }`}
          style={{
            background: "linear-gradient(to bottom, #7CB9E8 0%, #5A9FD4 100%)",
          }}
        >
          <div className="flex flex-col h-full text-white">
            {/* Logo & Hostel Info */}
            <div className="p-6 border-b border-white/20">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt="Logo"
                      className="w-10 h-10 object-contain rounded"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-white/20 rounded flex items-center justify-center">
                      <Home className="w-6 h-6" />
                    </div>
                  )}
                  <div className="flex-1">
                    <h1 className="text-xl font-bold leading-tight">
                      {settings.name || "DormCircle"}
                    </h1>
                    {shortAddress && (
                      <p className="text-xs opacity-90">{shortAddress}</p>
                    )}
                  </div>
                </div>
                <button
                  data-testid="close-sidebar-btn"
                  onClick={() => setSidebarOpen(false)}
                  className="lg:hidden"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <p className="text-sm opacity-90">Warden Portal</p>
            </div>

            {/* Menu Items */}
            <nav className="flex-1 overflow-y-auto p-4">
              <ul className="space-y-2">
                {menuItems.map((item) => (
                  <li key={item.path}>
                    <button
                      data-testid={item.testId}
                      onClick={() => {
                        navigate(item.path);
                        setSidebarOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                        location.pathname === item.path
                          ? "bg-white/20 shadow-md"
                          : "hover:bg-white/10"
                      }`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            {/* User Info & Logout */}
            <div className="p-4 border-t border-white/20">
              <div className="mb-4 p-3 bg-white/10 rounded-lg">
                <p className="font-semibold">{user?.name}</p>
                <p className="text-sm opacity-90">{user?.phone}</p>
                {!permissionsLoading && permissions && (
                  <div className="mt-2 pt-2 border-t border-white/20">
                    <p className="text-xs opacity-75 mb-1">
                      Permissions Active
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(permissions).filter(
                        ([key, value]) => value && key.startsWith("can_")
                      ).length > 0 ? (
                        <span className="text-xs bg-green-500/20 border border-green-500/30 px-2 py-0.5 rounded">
                          {
                            Object.entries(permissions).filter(
                              ([key, value]) => value && key.startsWith("can_")
                            ).length
                          }{" "}
                          Enabled
                        </span>
                      ) : (
                        <span className="text-xs bg-yellow-500/20 border border-yellow-500/30 px-2 py-0.5 rounded">
                          View Only
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="my-5">
                <CommonPasswordForgotModal role={"warden"} />
              </div>
              <Button
                data-testid="logout-btn"
                onClick={handleLogout}
                variant="outline"
                className="w-full border-white/30 text-white hover:bg-white/20"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <header className="bg-white shadow-sm p-4 flex items-center justify-between">
            <button
              data-testid="open-sidebar-btn"
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden"
            >
              <Menu className="w-6 h-6" style={{ color: "#2C5F7F" }} />
            </button>
            <h2 className="text-xl font-semibold" style={{ color: "#2C5F7F" }}>
              {menuItems.find((item) => item.path === location.pathname)
                ?.label || "Dashboard"}
            </h2>
            <div className="w-10"></div>
          </header>

          {/* Content Area */}
          <main className="flex-1 overflow-y-auto p-6">
            <Routes>
              <Route index element={<WardenHome permissions={permissions} />} />
              <Route
                path="students"
                element={<StudentManagement permissions={permissions} />}
              />
              <Route
                path="student-profile/:studentId"
                element={<StudentProfile permissions={permissions} />}
              />
              <Route
                path="bulk-updates"
                element={<BulkUpdates permissions={permissions} />}
              />
              <Route
                path="attendance"
                element={<AttendanceManagement permissions={permissions} />}
              />
              <Route
                path="health"
                element={<HealthRecords permissions={permissions} />}
              />
              <Route
                path="results"
                element={<ResultsManagement permissions={permissions} />}
              />
              <Route
                path="marksheets"
                element={<MarkSheets permissions={permissions} />}
              />
              <Route
                path="activities"
                element={<Activities permissions={permissions} />}
              />
              <Route
                path="nutrition"
                element={<WardenNutrition />}
              />
              <Route
                path="academic"
                element={<WardenAcademic />}
              />
              <Route
                path="gallery"
                element={<WardenGallery permissions={permissions} />}
              />
              <Route
                path="announcements"
                element={<AnnouncementsBoard permissions={permissions} />}
              />
              <Route path="calls" element={<CallRequests />} />
              <Route path="issues" element={<WardenIssues />} />
              <Route path="holidays" element={<HolidayView />} />
              {/* <Route path="settings" element={<WardenSettings />} /> */}
            </Routes>
          </main>
        </div>

        {/* Sidebar Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          ></div>
        )}
      </div>
    </>
  );
};

export default WardenDashboard;
